// @ts-strict-ignore
import partition from 'lodash/partition';

import {Section, SubLabelTheme} from 'common/components/items-menu/types';
import {getIconForPropertyType} from 'common/report/util/properties';
import {PropertyDefinition} from 'common/util/definitions-types';
import {FeatureGate, Permission, ProjectFeatureFlag, ResourceType} from 'common/report/constants';
import {Cohort as CohortModel} from 'common/query-builder/cohorts-query-builder/models/cohort';
import {isAllowedSuperadminOverride} from 'common/report/util/saved-entities';
import {MetricsResultsMap} from 'app_api/projects/metrics/types';
import {ActionType, ContextPanelData} from 'common/widgets/context-panel/types';
import {getTooltipDataForCohort, getTooltipDataForProperty} from 'common/widgets/context-panel/util';
import {CohortStore} from 'common/report/stores/cohorts';
import {
  SerializedCohort,
  SerializedInlineCohort,
} from 'common/query-builder/cohorts-query-builder/models/types/serialized-cohort-types';
import {BehaviorsResource} from 'common/report/model-resources/behaviors';
import {MetricsResource} from 'common/report/model-resources/metrics';
import {CurrentProject} from 'common/report/model-resources/projects';
import {
  getAllPropertyDefinitionsForGroupProfiles,
  getAllPropertyDefinitionsForUserProfiles,
} from 'common/report/stores/property-definitions';
import {
  eventPrefix as shareEventPrefix,
  ShareButtonUISource,
  track as trackShare,
} from 'common/widgets/sharing/tracking';
import {openEventsSidebar} from 'common/widgets/events-sidebar';
import {ContextPanelReactProps} from 'common/widgets/context-panel';
import {promptSharing} from 'common/widgets/sharing';
import {EntityTypeNameSingular, SharableEntityType} from 'common/report/util/saved-entities/types';
import {currentProjectStore} from 'common/report/stores/project-details';
import {eventDefinitionsStore} from 'common/report/stores/event-definitions';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import {showMetricsApiErrorToast, updateMetric} from 'common/report/queries/app-api/metrics';
import {PATH_SEPARATOR} from 'common/constants';
import {MetricType} from 'common/types/reports/bookmark';
import {LocalizationStore} from 'common/i18n/store';
import {generateSavedMetricContextPanelData} from 'common/query-builder/saved-metrics-menu/context-panel';
import {InlineCohortsModalProps} from 'common/widgets/inline-cohorts-modal/types';
import {isFirstClassMetricsEnabled} from 'common/report/util/feature-sampling';
import {
  MetricTypeIcons,
  OldMetricTypeIcons,
} from 'common/query-builder/query-entry-section/row-types/new-metric-entry/types/constants';

import {ChildMenuItemsCache, ContextPanelRendererParams} from '../child-menu';
import {NUM_RECENT_ITEMS, TRACKING_EVENTS} from '../constants';
import {CohortItem, isScdProperty} from '../properties-menu/types';
import {InlineCohortSegment} from '../segment-menu/types';
import {EventsController} from '../contexts/events-controller';
import {isEventItem} from '../events-menu/util';
import {generateBehaviorContextPanelData} from '../behaviors-menu/context-panel';
import {generateFormulaContextPanelData} from '../formulas-menu/context-panel';
import {getDataDefinitionsPropertyMenuItem} from '../properties-menu/util';
import {updateEntityDescription} from '../utils/util';
import {
  CohortItemValue,
  CohortSectionItem,
  isCohortMenuSectionItem,
  isEventMenuSectionItem,
  isFormulaMenuSectionItem,
  isFunnelMenuSectionItem,
  isProfileMenuSectionItem,
  isRetentionMenuSectionItem,
  isSavedMetricMenuSectionItem,
  MenuType,
  MenuTypeMap,
  MenuTypeToMetricType,
  MultiMetricsSection,
  MultiMetricsSectionItem,
  ProfileItemValue,
} from './types';

export function getRecentItems<T extends MenuType>(cache: ChildMenuItemsCache, menuType: T): Array<MenuTypeMap[T]> {
  return cache.getRecentItems(menuType, NUM_RECENT_ITEMS);
}

export function getProfileSections({
  dataGroupName,
  firstClassMetricsEnabled,
  historicalProperties,
}: {
  dataGroupName: string;
  firstClassMetricsEnabled: boolean;
  historicalProperties: PropertyDefinition[];
}): Array<MultiMetricsSection<ProfileItemValue>> {
  const iconMap = firstClassMetricsEnabled ? MetricTypeIcons : OldMetricTypeIcons;
  const sections: Array<MultiMetricsSection<ProfileItemValue>> = [
    {
      label: ({t}) => t(`query-builder.multi-metrics-menu.section-profile`),
      items: [
        {
          icon: iconMap[MetricType.People],
          label: ({t}) => t(`query-builder.multi-metrics-menu.analyze-profiles`),
          menuType: MenuType.Profile,
          value: `$all_people`,
        },
      ],
    },
  ];
  if (historicalProperties.length > 0) {
    sections.push({
      label: ({t}) => t(`query-builder.multi-metrics-menu.section-historical-profile-props`),
      items: historicalProperties.map((property) => {
        return {
          icon: getIconForPropertyType(property.type, {isScd: true}),
          label: property.label,
          subLabel: `${dataGroupName}${PATH_SEPARATOR}`,
          subLabelTheme: SubLabelTheme.Breadcrumb,
          menuType: MenuType.Profile,
          value: {
            resourceType: ResourceType.User,
            scdEvent: property.scdEvent,
            dataGroupId: property.dataGroupId,
            propertyDefaultType: property.type,
            type: property.type,
            value: property.name,
          },
        };
      }),
    });
  }

  return sections;
}

export function getCohortSections({
  recentCohortSectionItems,
  dataGroupId,
  userId,
  cohorts,
}: {
  recentCohortSectionItems: CohortSectionItem[];
  dataGroupId: string;
  userId: string | number;
  cohorts: CohortItem[];
}): Array<MultiMetricsSection<CohortItemValue>> {
  let inlineCohorts: CohortItem[] = [];
  let [userCohorts, allCohorts] = partition(cohorts, (cohort) => cohort.createdBy && cohort.createdBy.id === userId);
  [inlineCohorts, userCohorts] = partition(userCohorts, (cohort) => {
    if (typeof cohort.value !== `number`) {
      return cohort.value && cohort.value.cohort && cohort.value.cohort.raw_cohort;
    }
    return false;
  });
  allCohorts = allCohorts.filter((cohort) => cohort.canView);

  const getMenuItem = (cohort: CohortItem): CohortSectionItem => {
    let cohortItemValue: CohortItemValue;
    if (typeof cohort.value === `number`) {
      cohortItemValue = {
        ...(dataGroupId ? {dataGroupId} : {}),
        resourceType: ResourceType.Cohort,
        label: cohort.label,
        value: cohort.value,
      };
    } else {
      cohortItemValue = {
        resourceType: ResourceType.Cohort,
        label: cohort.label,
        value: cohort.value as InlineCohortSegment,
      };
    }
    return {
      icon: `community`,
      label: cohort.label,
      menuType: MenuType.Cohort,
      value: cohortItemValue,
    };
  };

  return [
    {
      label: ({t}) => t(`query-builder.multi-metrics-menu.section-recents`),
      isRecentSection: true,
      items: recentCohortSectionItems,
    },
    {
      label: ({t}) => t(`query-builder.multi-metrics-menu.section-cohorts`),
      items: [...inlineCohorts, ...userCohorts, ...allCohorts].map(getMenuItem),
    },
  ].filter(Boolean);
}

export function getCreateCustomSections({
  excludedMetricTypes,
  firstClassMetricsEnabled,
}: {
  excludedMetricTypes: Set<MetricType>;
  firstClassMetricsEnabled: boolean;
}): Array<Section<MenuType>> {
  const iconMap = firstClassMetricsEnabled ? MetricTypeIcons : OldMetricTypeIcons;
  return [
    {
      items: [
        {
          label: ({t}) => t(`query-builder.metrics.custom-event`),
          icon: `event`,
          value: MenuType.Event,
        },
      ].filter(
        (item) =>
          !excludedMetricTypes.has(MenuTypeToMetricType[item.value]) &&
          !excludedMetricTypes.has(MetricType.CustomEvent),
      ),
    },
    {
      items: [
        {
          label: ({t}) => t(`query-builder.metrics.formula`),
          icon: `formula`,
          value: MenuType.Formula,
        },
      ].filter((item) => !excludedMetricTypes.has(MenuTypeToMetricType[item.value])),
    },
    {
      items: [
        {
          label: ({t}) => t(`query-builder.metrics.funnel`),
          icon: iconMap[MetricType.Funnel],
          value: MenuType.Funnel,
        },
        {
          label: ({t}) => t(`query-builder.metrics.retention`),
          icon: iconMap[MetricType.Retention],
          value: MenuType.Retention,
        },
        {
          label: ({t}) => t(`query-builder.metrics.cohort`),
          icon: `community`,
          value: MenuType.Cohort,
        },
      ].filter((item) => !excludedMetricTypes.has(MenuTypeToMetricType[item.value])),
    },
  ];
}

export function getMultiMetricsItemValue(sectionItem: MultiMetricsSectionItem) {
  if (isEventMenuSectionItem(sectionItem) && isEventItem(sectionItem.value)) {
    return sectionItem.value.value;
  }
  if (isProfileMenuSectionItem(sectionItem)) {
    if (sectionItem.value !== `$all_people` && isScdProperty(sectionItem.value)) {
      return sectionItem.value.value;
    }
    return sectionItem.value;
  }
  if (isCohortMenuSectionItem(sectionItem)) {
    return sectionItem.value.label;
  }
  if (
    isFormulaMenuSectionItem(sectionItem) ||
    isFunnelMenuSectionItem(sectionItem) ||
    isRetentionMenuSectionItem(sectionItem)
  ) {
    return sectionItem.value.id;
  }
  return ``;
}

export function createInlineCohortModalAttrs({
  defaultCohort,
  dataGroupId,
  userId,
  projectId,
  workspaceId,
  projectHasCohorts,
  supportB2bCohorts,
  trackName,
}: {
  defaultCohort: CohortModel;
  dataGroupId: string;
  userId: number;
  projectId: number;
  workspaceId: number;
  projectHasCohorts: boolean;
  supportB2bCohorts: boolean;
  trackName: string;
}): InlineCohortsModalProps {
  return {
    canSave: projectHasCohorts,
    canEdit: isAllowedSuperadminOverride(defaultCohort) || defaultCohort.can_update_basic,
    dataGroupId: dataGroupId ?? null,
    canApplyUnsavedCohort: !defaultCohort?.id,
    name: defaultCohort?.name,
    open: true,
    projectHasCohorts,
    supportB2bCohorts,
    projectId,
    cohort: defaultCohort?.serialize(),
    trackName,
    userId,
    workspaceId,
    showCohortCount: true,
  };
}

export function getHistoricalProperties(dataGroupId: string, i18n: LocalizationStore) {
  if (dataGroupId) {
    return getAllPropertyDefinitionsForGroupProfiles()
      .filter((property) => property.dataGroupId === dataGroupId && !!property.scdEvent)
      .map((property) => getDataDefinitionsPropertyMenuItem(property, i18n));
  }
  return getAllPropertyDefinitionsForUserProfiles()
    .filter((property) => property.status !== `custom` && !!property.scdEvent)
    .map((property) => getDataDefinitionsPropertyMenuItem(property, i18n));
}

export function getTooltipData(
  sectionItem: MultiMetricsSectionItem,
  {
    dataGroupName,
    readonly,
    descriptionEditDisabled,
    hideEditControls,
    eventsController,
    cohortsStore,
    currentProject,
    behaviors,
    metrics,
    i18n,
  }: {
    dataGroupName: string;
    readonly: boolean;
    descriptionEditDisabled: boolean;
    hideEditControls: boolean;
    eventsController: EventsController;
    cohortsStore: CohortStore;
    currentProject: CurrentProject;
    behaviors: BehaviorsResource;
    metrics: MetricsResource;
    i18n: LocalizationStore;
  },
): ContextPanelData {
  if (!sectionItem || !sectionItem.value) {
    return null;
  }

  if (isEventMenuSectionItem(sectionItem)) {
    if (isEventItem(sectionItem.value)) {
      return eventsController.getContextPanelData(sectionItem.value, {
        allowEdit: !readonly && !hideEditControls,
      });
    }
    return null;
  } else if (isCohortMenuSectionItem(sectionItem)) {
    const cohortItemValue = sectionItem.value;
    let serializedCohort: SerializedCohort | SerializedInlineCohort | undefined;
    if (typeof cohortItemValue.value === `number`) {
      serializedCohort = cohortsStore.value[cohortItemValue.value];
    } else {
      const inlineCohortSegment: InlineCohortSegment = cohortItemValue.value;
      serializedCohort = cohortsStore.value[inlineCohortSegment.cohort.raw_cohort.unsavedId];
      if (!serializedCohort) {
        serializedCohort = inlineCohortSegment.cohort.raw_cohort;
      }
    }

    if (!serializedCohort) {
      return null;
    }

    const cohort = {
      ...cohortItemValue,
      name: serializedCohort.name,
      createdBy: serializedCohort.created_by,
    };
    return getTooltipDataForCohort(cohort);
  } else if (isFunnelMenuSectionItem(sectionItem) || isRetentionMenuSectionItem(sectionItem)) {
    const canWriteBehaviors = currentProject.hasPermission(Permission.WriteBehaviors);
    const canShare = currentProject.getFeatureGate(FeatureGate.Sharing);
    return generateBehaviorContextPanelData({
      behaviorsCtx: behaviors,
      canShare,
      canWriteBehaviors,
      firstClassMetricsEnabled: isFirstClassMetricsEnabled(currentProject),
      id: sectionItem.value.id,
    });
  } else if (isSavedMetricMenuSectionItem(sectionItem)) {
    const canWriteMetrics = currentProject.hasPermission(Permission.WriteMetrics);
    const canShare = currentProject.getFeatureGate(FeatureGate.Sharing);
    return generateSavedMetricContextPanelData({
      id: sectionItem.value.id,
      metricsContext: metrics,
      canWriteMetrics,
      canShare,
    });
  } else if (isFormulaMenuSectionItem(sectionItem)) {
    const canWriteMetrics = currentProject.hasPermission(Permission.WriteMetrics);
    const canShare = currentProject.getFeatureGate(FeatureGate.Sharing);
    return generateFormulaContextPanelData({
      id: sectionItem.value.id,
      metricsContext: metrics,
      canWriteMetrics,
      canShare,
    });
  } else if (
    isProfileMenuSectionItem(sectionItem) &&
    sectionItem.value !== `$all_people` &&
    isScdProperty(sectionItem.value)
  ) {
    const property = sectionItem.value;
    const historicalProperties = getHistoricalProperties(property.dataGroupId, i18n);
    const tooltipData = getTooltipDataForProperty(
      {
        name: property.value as string,
        resourceType: property.resourceType,
        dataType: property.type,
        dataGroupId: property.dataGroupId,
        dataGroupName,
        ...(property.customPropertyId ? {customPropertyId: String(property.customPropertyId)} : {}),
        path: [dataGroupName],
        isLookupTableProperty: false,
        isRecentlyUsed: false,
        i18n,
      },
      historicalProperties,
      {
        allowEdit: !hideEditControls && !readonly,
        allowDescriptionEdit: descriptionEditDisabled,
        lookupTables: {},
        iconReferenceType: `dataType`,
      },
    );
    return tooltipData;
  }

  return null;
}

export function createContextPanelEventHandlers({
  track,
  additionalArgs = {},
  availableContexts = {},
  handleHoverIn = null,
  handleHoverOut = null,
  handleInlineEvent,
  handleInlineCohort,
}: Partial<ContextPanelRendererParams> & {
  handleInlineEvent: (...args) => void;
  handleInlineCohort: (...args) => void;
}): Partial<ContextPanelReactProps> {
  const projectId = additionalArgs.projectId;
  const workspaceId = additionalArgs.workspaceId;

  const behaviors = availableContexts.behaviors;
  const metrics = availableContexts.metrics;

  const handleContextPanelAction = async (ev: CustomEvent) => {
    const action = ev.detail.name;
    const item = ev.detail.item;
    if (action === ActionType.ShareFormula || action === ActionType.ShareMetric) {
      const metricId = item.id;
      await promptSharing({
        type: SharableEntityType.Metric,
        id: metricId,
        'project-id': projectId,
      });
    } else if (action === ActionType.ShareBehavior) {
      const behaviorId = item.id;
      await promptSharing({
        type: SharableEntityType.Behavior,
        id: behaviorId,
        'project-id': projectId,
      });
    } else if (action === ActionType.EditCustomEvent) {
      const customEventId = item.id;
      handleInlineEvent(customEventId);
    } else if (action === ActionType.ShareCustomEvent) {
      const {eventName, props} = trackShare.shareButtonClick(ShareButtonUISource.ContextPanel);
      track(eventName, props, false, shareEventPrefix);
      promptSharing({
        type: SharableEntityType.CustomEvent,
        id: item.id,
        'project-id': projectId,
        'is-superadmin': item.superadminOverrideShare,
      });
    } else if (action === ActionType.EditCohort || Boolean(item.value?.cohort?.raw_cohort)) {
      handleInlineCohort(item);
      ev.stopPropagation();
    } else if (action === ActionType.ShareCohort || action === ActionType.ShareCustomProperty) {
      const isShareCohort = action === ActionType.ShareCohort;
      const {eventName, props} = trackShare.shareButtonClick(ShareButtonUISource.ContextPanel);
      track(`[${shareEventPrefix}] ${eventName}`, props);
      promptSharing({
        type: isShareCohort ? SharableEntityType.Cohort : SharableEntityType.CustomProperty,
        id: isShareCohort ? item.id : item.property.customPropertyId,
        'project-id': projectId,
        'is-superadmin': item.superadminOverrideShare,
      });
    } else if (action === ActionType.ViewSampleEvents && item.url) {
      track(TRACKING_EVENTS.SAMPLE_EVENT, {}, false);
      if (currentProjectStore.value?.hasFeatureFlag(ProjectFeatureFlag.EventsSidebar)) {
        openEventsSidebar({url: item.url});
      } else {
        window.open(item.url, `_blank`);
      }
    } else if ((action === ActionType.ViewEntity || action === ActionType.LookupTableInfo) && item.url) {
      window.open(item.url, `_blank`);
    } else {
      throw new Error(`action handler not implemented`);
    }
  };

  const handleContextPanelEditDescription = async ({detail}: CustomEvent) => {
    const updatedDescription = detail.description;
    const entityType = detail.item.type;

    if (entityType === EntityTypeNameSingular.Event || entityType === EntityTypeNameSingular.CustomEvent) {
      const eventStoreKey = detail.item.name;
      const IdName = detail.item.type === EntityTypeNameSingular.Event ? `id` : `customEventId`;

      const definition = eventDefinitionsStore.value[eventStoreKey];
      const descriptionBeforeUpdate = definition?.description ?? null;
      const count = definition?.count || 0;

      let eventDefinition = {[IdName]: detail.item.id, description: detail.description};
      if (!detail.item.id) {
        eventDefinition = {name: detail.item.name, description: detail.description};
      }
      const updateRequestObj = {
        projectId,
        workspaceId,
        eventDefinition,
      };

      const undoRequestObj = {
        ...updateRequestObj,
        eventDefinition: {...updateRequestObj.eventDefinition, description: descriptionBeforeUpdate},
      };

      await updateEntityDescription({
        entityStore: eventDefinitionsStore,
        newDescription: updatedDescription,
        storeKey: eventStoreKey,
        updateRequestObj,
        undoRequestObj,
        entityType,
        addToast,
        track,
      });
      eventDefinitionsStore.update({
        [eventStoreKey]: {
          ...eventDefinitionsStore.value[eventStoreKey],
          count,
        },
      });
    } else if (entityType === EntityTypeNameSingular.Metric) {
      const behaviorId = detail.item.id;
      try {
        await behaviors.patch(behaviorId, {description: updatedDescription});
      } catch {
        addToast({
          message: `Sorry, something went wrong. Please contact support if this problem persists.`,
          type: ToastType.ERROR,
          timeout: null,
        });
      }
    } else if (entityType === EntityTypeNameSingular.Behavior) {
      const metricId = detail.item.id;
      let results: MetricsResultsMap;
      try {
        results = await updateMetric({
          metricId,
          metric: {
            description: updatedDescription,
          },
        });
      } catch {
        results = null;
        showMetricsApiErrorToast();
      }
      if (results) {
        metrics.update(results, {partialUpdate: false});
      }
    }
  };

  return {
    onHoverIn: handleHoverIn,
    onHoverOut: handleHoverOut,
    onTooltipContextAction: handleContextPanelAction,
    onTooltipContextEditDescription: handleContextPanelEditDescription,
  };
}
